import { motion } from "framer-motion";
import {
  Clock,
  Database,
  CreditCard,
  Bell,
  TrendingUp,
  Calendar,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const OutcomesSection = () => {
  const outcomes = [
    {
      icon: <Clock className="w-7 h-7" />,
      title: "Save 10+ Hours Weekly",
      description: "Automate admin tasks that used to eat up your weekends.",
      proof: "Average time saved by club admins",
      color: "from-blue-500/20 to-cyan-500/20",
      borderColor: "group-hover:border-blue-500/50",
    },
    {
      icon: <Database className="w-7 h-7" />,
      title: "Never Lose Player Data",
      description:
        "Everything in one place — no more scattered chats and lost spreadsheets.",
      proof: "100% data retention rate",
      color: "from-violet-500/20 to-purple-500/20",
      borderColor: "group-hover:border-violet-500/50",
    },
    {
      icon: <CreditCard className="w-7 h-7" />,
      title: "Get Paid On Time",
      description:
        "Automated payment tracking so nothing falls through the cracks.",
      proof: "85% faster fee collection",
      color: "from-emerald-500/20 to-green-500/20",
      borderColor: "group-hover:border-emerald-500/50",
    },
    {
      icon: <Bell className="w-7 h-7" />,
      title: "Keep Parents Informed",
      description: "Real-time updates without endless WhatsApp messages.",
      proof: "90% parent satisfaction",
      color: "from-amber-500/20 to-orange-500/20",
      borderColor: "group-hover:border-amber-500/50",
    },
    {
      icon: <TrendingUp className="w-7 h-7" />,
      title: "Develop Players Faster",
      description:
        "Track progress visually and identify areas for improvement.",
      proof: "2x faster player development",
      color: "from-rose-500/20 to-pink-500/20",
      borderColor: "group-hover:border-rose-500/50",
    },
    {
      icon: <Calendar className="w-7 h-7" />,
      title: "Simplify Scheduling",
      description:
        "Match fixtures, training sessions, and venue bookings all in one calendar.",
      proof: "Zero scheduling conflicts",
      color: "from-cyan-500/20 to-teal-500/20",
      borderColor: "group-hover:border-cyan-500/50",
    },
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  return (
    <motion.section
      className="min-h-screen flex items-center py-12 px-6 bg-background relative overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={staggerContainer}
    >
      {/* Background gradient */}
      <div className="absolute inset-0 opacity-40">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[300px] bg-accent-soft/10 rounded-full blur-3xl" />
      </div>

      {/* Decorative grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
          backgroundSize: "40px 40px",
        }}
      />

      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Section Header */}
        <motion.div className="text-center mb-16" variants={fadeInUp}>
          <motion.span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/30 text-accent text-sm font-semibold mb-6">
            <Sparkles className="w-4 h-4" />
            Why Clubs Choose FanJago
          </motion.span>
          <h2 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-foreground mb-6">
            Real Results, Not Just Features
          </h2>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
            Join 50+ clubs already transforming how they manage football
          </p>
        </motion.div>

        {/* Outcomes Grid */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12"
          variants={staggerContainer}
        >
          {outcomes.map((outcome, index) => (
            <motion.div
              key={outcome.title}
              className={`group relative p-8 rounded-2xl bg-surface/50 border border-border/50 backdrop-blur-sm ${outcome.borderColor} hover:bg-surface/80 transition-all duration-500 overflow-hidden`}
              variants={fadeInUp}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
            >
              {/* Gradient background glow */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${outcome.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
              />

              {/* Corner accent */}
              <div className="absolute -top-12 -right-12 w-32 h-32 bg-accent/5 rounded-full blur-2xl group-hover:bg-accent/10 transition-all duration-500" />

              {/* Bottom line accent */}
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Content wrapper */}
              <div className="relative z-10">
                {/* Icon */}
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-accent/20 to-accent/5 border border-accent/30 mb-6 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-accent/20 transition-all duration-300">
                  <motion.span
                    className="text-accent"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                  >
                    {outcome.icon}
                  </motion.span>
                </div>

                <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground mb-3">
                  {outcome.title}
                </h3>

                <p className="text-muted-foreground mb-6 leading-relaxed">
                  {outcome.description}
                </p>

                {/* Enhanced stat badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20">
                  <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                  <span className="text-sm text-accent font-semibold">
                    {outcome.proof}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Micro CTA */}
        <motion.div className="text-center" variants={fadeInUp}>
          <Button
            variant="outline"
            size="lg"
            className="group"
            onClick={() =>
              document
                .querySelector("#features")
                ?.scrollIntoView({ behavior: "smooth" })
            }
          >
            See All Features
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default OutcomesSection;
